/**
 * Use Case: Should Filter Out Blocked User
 *
 * Synchronous, in-memory predicate used by user-list flows (subscribers,
 * subscriptions, search, etc.) to drop entries that touch a blocked
 * relationship with the current user.
 *
 * Defence-in-depth: the `blockUser` cloud function already inactivates both
 * sides of the affected subscription documents
 * (`cloud_functions/functions/src/functions/user/block_user.ts:81-97`), and
 * the subscriptions query filters `status in [active, cancelled]`. This
 * predicate catches the race window before that cleanup lands locally — and
 * any drift between `blockedUsers` / `usersBlockedMe` and the live
 * subscriptions list.
 *
 * Intentionally NOT checked here: the target user's `blocked` flag.
 * Verifying that would require a per-row server fetch, which is too
 * expensive for list rendering.
 */

import type { UserEntity } from '@/domain/entities/user/userEntity';

export class ShouldFilterOutBlockedUserUseCase {
    execute(userId: string, currentUser: UserEntity | null): boolean {
        if (!currentUser) {
            return false;
        }

        if (currentUser.blockedUsers?.includes(userId)) {
            return true;
        }

        if (currentUser.usersBlockedMe?.includes(userId)) {
            return true;
        }

        return false;
    }
}

export const shouldFilterOutBlockedUserUseCase = new ShouldFilterOutBlockedUserUseCase();
